import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Truck,
  Search,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Gauge,
  RefreshCw,
  FileSpreadsheet,
  TrendingUp,
  Activity,
  AlertCircle,
  History,
  List,
  Plus,
  ClipboardList,
  User,
} from "lucide-react";
import { FleetRequirementsModal } from "./FleetRequirementsModal";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { toast } from "react-hot-toast";
import { VehicleHistoryModal } from "./VehicleHistoryModal";
import { CreateOTModal } from "./CreateOTModal";
import { VehiclePlanDetailsModal } from "./VehiclePlanDetailsModal";
import DetalleOrden from "./DetalleOrden";

interface VehiculoStatus {
  id: string;
  placa: string;
  modelo: string;
  marca: string;
  vehiculo_estado: string;
  ot_estado: "ABIERTA" | "EN_PROGRESO" | null;
  encargado: string | null;
  plan_nombre: string | null;
  asignacion_id: number | null;
  km_actual: number | null;
  estado_mantenimiento: "NORMAL" | "ALERTA" | "CRITICO" | "SIN_PLAN";
  rutina_ultima: any | null;
  rutina_siguiente: any | null;
  rutinas: Array<{
    id: number;
    nombre: string;
    tipo: string;
    km_restantes: number;
    estado: "NORMAL" | "ALERTA" | "CRITICO";
  }>;
}

export default function GestionFlota() {
  const [vehiculos, setVehiculos] = useState<VehiculoStatus[]>(() => {
    try {
      const stored = sessionStorage.getItem('vilox_cache_flota');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(vehiculos.length === 0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedVehiculo, setSelectedVehiculo] = useState<{
    id: string;
    placa: string;
    marca: string;
    modelo: string;
  } | null>(null);
  const [isCreateOTOpen, setIsCreateOTOpen] = useState(false);
  const [selectedVehiculoForOT, setSelectedVehiculoForOT] = useState<any>(null);
  const [isPlanDetailsOpen, setIsPlanDetailsOpen] = useState(false);
  const [selectedVehiculoForPlan, setSelectedVehiculoForPlan] = useState<{
    id: string;
    placa: string;
    marca: string;
    modelo: string;
    plan_nombre: string | null;
  } | null>(null);

  const [viewingOtId, setViewingOtId] = useState<number | null>(null);
  const [reopenHistoryAfterOt, setReopenHistoryAfterOt] = useState(false);

  const [activeFilter, setActiveFilter] = useState<'ALL' | 'OPERATIVO' | 'MANTENIMIENTO' | 'CRITICO' | 'ALERTA'>('ALL');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 8;

  // Estados para auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(86400);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Estado para reporte
  const [generandoReporte, setGenerandoReporte] = useState(false);
  const [isRequirementsOpen, setIsRequirementsOpen] = useState(false);

  // Estados para estadísticas
  const [stats, setStats] = useState(() => {
    try {
      const stored = sessionStorage.getItem('vilox_cache_flota_stats');
      return stored ? JSON.parse(stored) : {
        total: 0,
        operativos: 0,
        enMantenimiento: 0,
        criticos: 0,
        alertas: 0,
        disponibilidad: 0,
      };
    } catch {
      return {
        total: 0,
        operativos: 0,
        enMantenimiento: 0,
        criticos: 0,
        alertas: 0,
        disponibilidad: 0,
      };
    }
  });

  const fetchFlota = async (showToast = false) => {
    try {
      if (showToast) setLoading(true);
      const response = await maintenanceService.getFlotaStatus();
      setVehiculos(response.data);
      sessionStorage.setItem('vilox_cache_flota', JSON.stringify(response.data));
      setLastUpdate(new Date());
      setCountdown(86400);

      // Calcular estadísticas
      const newStats = calcularEstadisticas(response.data);
      sessionStorage.setItem('vilox_cache_flota_stats', JSON.stringify(newStats));
    } catch (error) {
      console.error("Error fetching fleet:", error);
      toast.error("Error al cargar la flota");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncKM = async () => {
    try {
      setLoading(true);

      // 🧠 INVALIDACIÓN: Al sincronizar GPS, forzamos limpieza de todas las rutinas 
      // para que se recalculen con el nuevo KM si el usuario abre una OT.
      maintenanceService.clearRoutinesCache();

      await fetchFlota();
      toast.success("Kilometrajes actualizados desde GPS", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error syncing KM:", error);
      toast.error("Error al sincronizar kilometrajes");
    } finally {
      setLoading(false);
    }
  };

  const calcularEstadisticas = (vehiculos: VehiculoStatus[]) => {
    const total = vehiculos.length;
    const operativos = vehiculos.filter(
      (v) => v.vehiculo_estado === "OPERATIVO",
    ).length;
    const enMantenimiento = vehiculos.filter(
      (v) => v.vehiculo_estado === "MANTENIMIENTO",
    ).length;
    const criticos = vehiculos.filter(
      (v) => v.estado_mantenimiento === "CRITICO",
    ).length;
    const alertas = vehiculos.filter(
      (v) => v.estado_mantenimiento === "ALERTA",
    ).length;
    const disponibilidad =
      total > 0 ? Math.round((operativos / total) * 100) : 0;

    const newStats = {
      total,
      operativos,
      enMantenimiento,
      criticos,
      alertas,
      disponibilidad,
    };
    setStats(newStats);
    return newStats;
  };

  const handleGenerarReporte = async () => {
    try {
      setGenerandoReporte(true);

      const response = await maintenanceService.generarReporteEjecutivo();

      // Crear blob y descargar el archivo
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fecha = new Date().toISOString().split("T")[0];
      link.download = `Reporte_Ejecutivo_Mantenimiento_${fecha}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("✅ Reporte ejecutivo generado exitosamente", {
        duration: 3000,
        icon: "📊",
      });
    } catch (error) {
      console.error("Error generando reporte:", error);
      toast.error("❌ Error al generar el reporte");
    } finally {
      setGenerandoReporte(false);
    }
  };

  useEffect(() => {
    fetchFlota();

    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        console.log("🔄 Actualizando kilometrajes desde GPS...");
        fetchFlota();
      }, 86400000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  useEffect(() => {
    if (autoRefresh) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 86400;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountdown(86400);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [autoRefresh, lastUpdate]);

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    if (!autoRefresh) {
      toast.success("Auto-actualización activada (cada 24 horas)");
    } else {
      toast("Auto-actualización desactivada", { icon: "⏸️" });
    }
  };

  const formatCountdown = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${mins}m ${secs}s`;
  };

  const openHistory = (vehiculo: VehiculoStatus) => {
    setSelectedVehiculo({
      id: vehiculo.id,
      placa: vehiculo.placa,
      marca: vehiculo.marca,
      modelo: vehiculo.modelo,
    });
    setIsHistoryOpen(true);
  };

  const handleViewOtFromHistory = (otId: number) => {
    setViewingOtId(otId);
    setReopenHistoryAfterOt(true);
    setIsHistoryOpen(false);
  };

  const handleBackFromOt = () => {
    setViewingOtId(null);
    if (reopenHistoryAfterOt) {
      setIsHistoryOpen(true);
      setReopenHistoryAfterOt(false);
    }
    // 🔥 IMPORTANTE: Al volver de una OT, refrescar el estado de la flota
    // para que se actualicen los KPIs y las etiquetas de los vehículos.
    fetchFlota();
  };

  const openCreateOT = async (vehiculo: any) => {
    if (!vehiculo.asignacion_id) {
      toast.error("El vehículo no tiene un plan asignado");
      return;
    }

    const loadingToast = toast.loading("Obteniendo detalles de rutinas...");
    try {
      // ✅ El servicio ahora maneja la caché de sesión (persistente entre pestañas/navegación)
      const routinesData = await maintenanceService.getVehicleRoutines(vehiculo.id);

      const vehiculoWithFullRoutines = {
        ...vehiculo,
        rutinas: routinesData
      };

      setSelectedVehiculoForOT(vehiculoWithFullRoutines);
      setIsCreateOTOpen(true);

      // Dismiss toast AFTER showing the modal to avoid "dead space"
      setTimeout(() => toast.dismiss(loadingToast), 300);
    } catch (error) {
      console.error("Error fetching routines for OT:", error);
      toast.error("No se pudieron cargar los detalles de las rutinas");
      toast.dismiss(loadingToast);
    }
  };

  const openPlanDetails = (vehiculo: VehiculoStatus) => {
    setSelectedVehiculoForPlan({
      id: vehiculo.id,
      placa: vehiculo.placa,
      marca: vehiculo.marca,
      modelo: vehiculo.modelo,
      plan_nombre: vehiculo.plan_nombre
    });
    setIsPlanDetailsOpen(true);
  };

  const filteredVehiculos = vehiculos.filter(
    (v) => {
      const matchesSearch = v.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.modelo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.marca?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      switch (activeFilter) {
        case 'OPERATIVO': return v.vehiculo_estado === 'OPERATIVO';
        case 'MANTENIMIENTO': return v.vehiculo_estado === 'MANTENIMIENTO';
        case 'CRITICO': return v.estado_mantenimiento === 'CRITICO';
        case 'ALERTA': return v.estado_mantenimiento === 'ALERTA';
        default: return true;
      }
    }
  );

  const totalPages = Math.ceil(filteredVehiculos.length / PAGE_SIZE);
  const paginatedVehiculos = filteredVehiculos.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Reset to page 1 when filter/search changes
  const handleFilterChange = (filter: 'ALL' | 'OPERATIVO' | 'MANTENIMIENTO' | 'CRITICO' | 'ALERTA') => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const getStatusInfo = (estado: string) => {
    switch (estado) {
      case "CRITICO":
        return {
          color: "text-red-600 bg-red-50 border-red-100",
          icon: AlertTriangle,
          label: "Critico",
          bgColor: "bg-red-600",
        };
      case "ALERTA":
        return {
          color: "text-orange-600 bg-orange-50 border-orange-100",
          icon: AlertTriangle,
          label: "Próximo",
          bgColor: "bg-orange-600",
        };
      case "NORMAL":
        return {
          color: "text-green-600 bg-green-50 border-green-100",
          icon: CheckCircle2,
          label: "Al día",
          bgColor: "bg-green-600",
        };
      case "MANTENIMIENTO_ABIERTA":
        return {
          color: "text-orange-600 bg-orange-50 border-orange-100",
          icon: Activity,
          label: "OT Abierta",
          bgColor: "bg-orange-600",
        };
      case "MANTENIMIENTO_PROGRESO":
        return {
          color: "text-orange-600 bg-orange-50 border-orange-100",
          icon: Activity,
          label: "En Taller",
          bgColor: "bg-orange-600",
        };
      default:
        return {
          color: "text-gray-500 bg-gray-50 border-gray-100",
          icon: Clock,
          label: "Sin Plan",
          bgColor: "bg-gray-400",
        };
    }
  };

  return (
    <div className="space-y-6">
      {viewingOtId ? (
        <DetalleOrden
          otId={viewingOtId}
          onBack={handleBackFromOt}
          onUpdate={() => fetchFlota()}
        />
      ) : (
        <div className="space-y-4">
          {/* Header con estadísticas */}
          <Card className="shadow-lg border-t-4 border-t-blue-600">
            <CardHeader className="p-6 pb-4">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                {/* Título + buscador */}
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-2xl font-bold text-gray-800 tracking-tight">
                    Gestión de Flota Vehicular
                  </CardTitle>
                  <p className="text-sm text-gray-500 font-medium mt-1">
                    Seguimiento integral de mantenimiento preventivo
                    {lastUpdate && (
                      <span className="ml-2 text-xs text-green-600 font-medium">
                        • Actualizado: {lastUpdate.toLocaleTimeString()}
                      </span>
                    )}
                  </p>
                </div>

                {/* Botones de acción */}
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    onClick={() => setIsRequirementsOpen(true)}
                    variant="outline"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 font-semibold"
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Previsión Items
                  </Button>

                  <Button
                    onClick={handleGenerarReporte}
                    disabled={generandoReporte}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md transition-all duration-300"
                  >
                    {generandoReporte ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Reporte Ejecutivo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Tarjetas de Estadísticas */}
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                <div
                  onClick={() => handleFilterChange('ALL')}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${activeFilter === 'ALL' ? 'bg-blue-600 border-blue-700 shadow-md text-white' : 'bg-blue-50 border-blue-200 hover:shadow-md hover:border-blue-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${activeFilter === 'ALL' ? 'text-blue-100' : 'text-blue-600'}`}>Total Flota</p>
                      <p className={`text-2xl font-bold mt-0.5 ${activeFilter === 'ALL' ? 'text-white' : 'text-blue-900'}`}>{stats.total}</p>
                    </div>
                    <Truck className={`h-7 w-7 opacity-60 ${activeFilter === 'ALL' ? 'text-white' : 'text-blue-500'}`} />
                  </div>
                </div>

                <div
                  onClick={() => handleFilterChange('OPERATIVO')}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${activeFilter === 'OPERATIVO' ? 'bg-green-600 border-green-700 shadow-md text-white' : 'bg-green-50 border-green-200 hover:shadow-md hover:border-green-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${activeFilter === 'OPERATIVO' ? 'text-green-100' : 'text-green-600'}`}>Operativos</p>
                      <p className={`text-2xl font-bold mt-0.5 ${activeFilter === 'OPERATIVO' ? 'text-white' : 'text-green-900'}`}>{stats.operativos}</p>
                    </div>
                    <CheckCircle2 className={`h-7 w-7 opacity-60 ${activeFilter === 'OPERATIVO' ? 'text-white' : 'text-green-500'}`} />
                  </div>
                </div>

                <div
                  onClick={() => handleFilterChange('MANTENIMIENTO')}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${activeFilter === 'MANTENIMIENTO' ? 'bg-orange-500 border-orange-600 shadow-md' : 'bg-orange-50 border-orange-200 hover:shadow-md hover:border-orange-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${activeFilter === 'MANTENIMIENTO' ? 'text-orange-100' : 'text-orange-600'}`}>Mantenim.</p>
                      <p className={`text-2xl font-bold mt-0.5 ${activeFilter === 'MANTENIMIENTO' ? 'text-white' : 'text-orange-900'}`}>{stats.enMantenimiento}</p>
                    </div>
                    <Activity className={`h-7 w-7 opacity-60 ${activeFilter === 'MANTENIMIENTO' ? 'text-white' : 'text-orange-500'}`} />
                  </div>
                </div>

                <div
                  onClick={() => handleFilterChange('CRITICO')}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${activeFilter === 'CRITICO' ? 'bg-red-600 border-red-700 shadow-md' : 'bg-red-50 border-red-200 hover:shadow-md hover:border-red-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${activeFilter === 'CRITICO' ? 'text-red-100' : 'text-red-600'}`}>Críticos</p>
                      <p className={`text-2xl font-bold mt-0.5 ${activeFilter === 'CRITICO' ? 'text-white' : 'text-red-900'}`}>{stats.criticos}</p>
                    </div>
                    <AlertCircle className={`h-7 w-7 opacity-60 ${activeFilter === 'CRITICO' ? 'text-white' : 'text-red-500'}`} />
                  </div>
                </div>

                <div
                  onClick={() => handleFilterChange('ALERTA')}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${activeFilter === 'ALERTA' ? 'bg-yellow-500 border-yellow-600 shadow-md' : 'bg-yellow-50 border-yellow-200 hover:shadow-md hover:border-yellow-400'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${activeFilter === 'ALERTA' ? 'text-yellow-100' : 'text-yellow-600'}`}>Alertas</p>
                      <p className={`text-2xl font-bold mt-0.5 ${activeFilter === 'ALERTA' ? 'text-white' : 'text-yellow-900'}`}>{stats.alertas}</p>
                    </div>
                    <AlertTriangle className={`h-7 w-7 opacity-60 ${activeFilter === 'ALERTA' ? 'text-white' : 'text-yellow-500'}`} />
                  </div>
                </div>

                <div className="p-3 rounded-xl border bg-purple-50 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide">Disponib.</p>
                      <p className="text-2xl font-bold text-purple-900 mt-0.5">{stats.disponibilidad}%</p>
                    </div>
                    <TrendingUp className="h-7 w-7 text-purple-500 opacity-60" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barra de búsqueda y controles */}
          <Card className="shadow-sm">
            <CardContent className="p-3">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                {/* Buscador */}
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por placa, modelo o marca..."
                    className="pl-9 h-9 border-gray-200 focus:border-blue-400"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>

                {/* Controles derecha */}
                <div className="flex gap-2 items-center">
                  {autoRefresh && (
                    <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                      <Clock className="h-3 w-3 text-green-600" />
                      <span className="text-xs text-green-700 font-medium">
                        Próxima actualización: {formatCountdown(countdown)}
                      </span>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncKM}
                    disabled={loading}
                    className="h-9 border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar GPS
                  </Button>

                  <Button
                    size="sm"
                    onClick={toggleAutoRefresh}
                    className={`h-9 ${autoRefresh ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    Auto {autoRefresh ? 'ON' : 'OFF'}
                  </Button>

                  {/* Contador de resultados */}
                  <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                    {filteredVehiculos.length} vehículo{filteredVehiculos.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Vehículos */}
          <Card className="shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-500 font-medium">Cargando estado de la flota...</p>
                  <p className="text-xs text-gray-400 mt-2">Sincronizando con GPS y base de datos</p>
                </div>
              ) : filteredVehiculos.length === 0 ? (
                <div className="text-center py-16">
                  <Truck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No se encontraron vehículos</p>
                  <p className="text-xs text-gray-400 mt-2">Intenta con otros términos de búsqueda</p>
                </div>
              ) : (
                <>
                  {/* Tabla de vehículos */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Vehículo</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Estado</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Kilometraje</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Plan</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Próxima Rutina</th>
                          <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                  {paginatedVehiculos.map((vehiculo) => {
                    const statusInfo = getStatusInfo(
                      vehiculo.vehiculo_estado === "MANTENIMIENTO"
                        ? (vehiculo.ot_estado === "ABIERTA" ? "MANTENIMIENTO_ABIERTA" : "MANTENIMIENTO_PROGRESO")
                        : vehiculo.estado_mantenimiento
                    );
                    const borderColor =
                      vehiculo.estado_mantenimiento === "CRITICO" ? "#ef4444"
                      : vehiculo.estado_mantenimiento === "ALERTA" ? "#f97316"
                      : vehiculo.estado_mantenimiento === "NORMAL" ? "#22c55e"
                      : "#9ca3af";
                    return (
                      <tr
                        key={vehiculo.id}
                        className="hover:bg-blue-50/30 transition-colors group border-l-4"
                        style={{ borderLeftColor: borderColor }}
                      >
                        {/* Vehículo */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors shrink-0">
                              <Truck className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 text-sm">{vehiculo.placa}</p>
                              <p className="text-xs text-gray-500">{vehiculo.marca} {vehiculo.modelo}</p>
                              {vehiculo.encargado && (
                                <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                                  <User className="w-3 h-3 text-blue-400" />
                                  {vehiculo.encargado}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Estado */}
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`${statusInfo.color} flex items-center gap-1 w-fit`}>
                            <statusInfo.icon className="h-3 w-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>

                        {/* Kilometraje */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm">
                            <Gauge className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-semibold text-gray-800">
                              {(vehiculo.km_actual || 0).toLocaleString()} km
                            </span>
                          </div>
                        </td>

                        {/* Plan */}
                        <td className="px-4 py-3">
                          {vehiculo.plan_nombre ? (
                            <button
                              onClick={() => openPlanDetails(vehiculo)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline text-left max-w-[160px] truncate block"
                            >
                              {vehiculo.plan_nombre}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Sin plan asignado</span>
                          )}
                        </td>

                        {/* Próxima Rutina */}
                        <td className="px-4 py-3">
                          {vehiculo.rutina_siguiente ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-semibold text-gray-700 truncate max-w-[140px]">
                                {vehiculo.rutina_siguiente.nombre}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <div className="flex-1 bg-blue-100 rounded-full h-1.5 w-20 overflow-hidden">
                                  <div
                                    className="h-1.5 rounded-full bg-blue-500"
                                    style={{ width: `${Math.max(5, Math.min(100, 100 - (vehiculo.rutina_siguiente.km_restantes / 5000) * 100))}%` }}
                                  />
                                </div>
                                <span className="text-[11px] font-bold text-blue-600 whitespace-nowrap">
                                  en {vehiculo.rutina_siguiente.km_restantes.toLocaleString()} km
                                </span>
                              </div>
                            </div>
                          ) : vehiculo.rutina_ultima && vehiculo.rutina_ultima.km_restantes < 0 ? (
                            <span className="text-xs font-bold text-red-600 animate-pulse">
                              ⚠ Vencida {Math.abs(vehiculo.rutina_ultima.km_restantes).toLocaleString()} km
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Sin próximas rutinas</span>
                          )}
                        </td>

                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2.5 gap-1 text-gray-600 border-gray-200 hover:bg-gray-100"
                              onClick={() => openHistory(vehiculo)}
                            >
                              <History className="w-3.5 h-3.5" />
                              <span className="text-xs">Historial</span>
                            </Button>
                            {vehiculo.asignacion_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2.5 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => openPlanDetails(vehiculo)}
                              >
                                <List className="w-3.5 h-3.5" />
                                <span className="text-xs">Detalles</span>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              className="h-8 px-2.5 gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => openCreateOT(vehiculo)}
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span className="text-xs">Crear OT</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginación */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                      <p className="text-xs text-gray-500">
                        Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredVehiculos.length)} de {filteredVehiculos.length} vehículos
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          ‹
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <Button
                            key={page}
                            variant={page === currentPage ? 'default' : 'outline'}
                            size="sm"
                            className={`h-8 w-8 p-0 text-xs ${page === currentPage ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          ›
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {selectedVehiculo && (
        <VehicleHistoryModal
          open={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          vehiculoId={selectedVehiculo.id}
          placa={selectedVehiculo.placa}
          marca={selectedVehiculo.marca}
          modelo={selectedVehiculo.modelo}
          onViewOt={handleViewOtFromHistory}
        />
      )}

      {selectedVehiculoForOT && (
        <CreateOTModal
          key={selectedVehiculoForOT.id}
          open={isCreateOTOpen}
          onClose={() => {
            setIsCreateOTOpen(false);
            setSelectedVehiculoForOT(null);
          }}
          vehiculo={selectedVehiculoForOT}
          onSuccess={fetchFlota}
        />
      )}

      {selectedVehiculoForPlan && (
        <VehiclePlanDetailsModal
          open={isPlanDetailsOpen}
          onClose={() => setIsPlanDetailsOpen(false)}
          vehiculoId={selectedVehiculoForPlan.id}
          placa={selectedVehiculoForPlan.placa}
          marca={selectedVehiculoForPlan.marca}
          modelo={selectedVehiculoForPlan.modelo}
          planNombre={selectedVehiculoForPlan.plan_nombre}
        />
      )}

      <FleetRequirementsModal
        open={isRequirementsOpen}
        onClose={() => setIsRequirementsOpen(false)}
      />
    </div>
  );
}
